import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";
import { useLocation, useNavigate } from "react-router-dom";

import { DashboardFooter } from "../layout/DashboardFooter";
import { DashboardHeader } from "../layout/DashboardHeader";
import { ZeosApiContext } from "../../../context/ZeosApiContext";
import { IDataMinerIncentiveItem } from "../../../services/zeos-api/contracts/DataMiner";
import { ENV } from "../../../env";
import { buildDataMinerQuery, parseDataMinerQuery } from "./dataMinerQuery";

type IncentiveRow = {
  category: string;
  categorySlug: string;
  filterCategory?: string;
  count: number;
  admin: string;
  notes: string;
};

type SampleLookup = {
  id: string;
  label: string;
  address: string;
  buildingType: string;
};

type LookupResult = SampleLookup & {
  formattedAddress?: string | null;
  county?: string | null;
  stateCode?: string | null;
  censusTractId?: string | null;
  dacPercentile?: number | null;
  isDac?: boolean | null;
  latitude?: number | null;
  longitude?: number | null;
  status?: string;
};

const sampleLookups: SampleLookup[] = [
  {
    id: "la-city-hall",
    label: "Los Angeles City Hall",
    address: "200 N Spring St, Los Angeles, CA 90012",
    buildingType: "Government",
  },
  {
    id: "la-convention-center",
    label: "Los Angeles Convention Center",
    address: "1201 S Figueroa St, Los Angeles, CA 90015",
    buildingType: "Convention",
  },
  {
    id: "lax",
    label: "Los Angeles International Airport",
    address: "1 World Way, Los Angeles, CA 90045",
    buildingType: "Airport",
  },
  {
    id: "union-station",
    label: "Los Angeles Union Station",
    address: "800 N Alameda St, Los Angeles, CA 90012",
    buildingType: "Transit",
  },
  {
    id: "port-of-la",
    label: "Port of Los Angeles",
    address: "425 S Palos Verdes St, San Pedro, CA 90731",
    buildingType: "Industrial",
  },
  {
    id: "crypto-arena",
    label: "Crypto.com Arena",
    address: "1111 S Figueroa St, Los Angeles, CA 90015",
    buildingType: "Arena",
  },
  {
    id: "usc",
    label: "USC University Park Campus",
    address: "3551 Trousdale Pkwy, Los Angeles, CA 90089",
    buildingType: "University",
  },
  {
    id: "ucla",
    label: "UCLA",
    address: "405 Hilgard Ave, Los Angeles, CA 90095",
    buildingType: "University",
  },
  {
    id: "cedars",
    label: "Cedars-Sinai Medical Center",
    address: "8700 Beverly Blvd, Los Angeles, CA 90048",
    buildingType: "Hospital",
  },
  {
    id: "getty",
    label: "Getty Center",
    address: "1200 Getty Center Dr, Los Angeles, CA 90049",
    buildingType: "Museum",
  },
  {
    id: "la-library",
    label: "Los Angeles Central Library",
    address: "630 W 5th St, Los Angeles, CA 90071",
    buildingType: "Library",
  },
  {
    id: "santa-monica",
    label: "Santa Monica City Hall",
    address: "1685 Main St, Santa Monica, CA 90401",
    buildingType: "Government",
  },
  {
    id: "long-beach",
    label: "Long Beach City Hall",
    address: "411 W Ocean Blvd, Long Beach, CA 90802",
    buildingType: "Government",
  },
  {
    id: "burbank-airport",
    label: "Hollywood Burbank Airport",
    address: "2627 N Hollywood Way, Burbank, CA 91505",
    buildingType: "Airport",
  },
  {
    id: "sofi",
    label: "SoFi Stadium",
    address: "1001 S Stadium Dr, Inglewood, CA 90301",
    buildingType: "Stadium",
  },
];

export const DataMiner = () => {
  const api = useContext(ZeosApiContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [showPriorityOnly, setShowPriorityOnly] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [rows, setRows] = useState<IncentiveRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [selectedLocation, setSelectedLocation] = useState("Los Angeles County, CA");
  const [selectedGeographyType, setSelectedGeographyType] = useState("County");
  const [selectedUtility, setSelectedUtility] = useState("LADWP (auto)");
  const [selectedIsDac, setSelectedIsDac] = useState<boolean | undefined>(undefined);
  const [lookupQuery, setLookupQuery] = useState("");
  const [lookupResult, setLookupResult] = useState<any | null>(null);
  const [lookupStatus, setLookupStatus] = useState<string | null>(null);
  const [lookupResults, setLookupResults] = useState<LookupResult[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupBatchStatus, setLookupBatchStatus] = useState<string | null>(null);
  const queryFilters = useMemo(
    () => parseDataMinerQuery(location.search),
    [location.search]
  );
  const hasAppliedAddress = useRef(false);

  const { isLoaded: isMapLoaded } = useJsApiLoader({
    googleMapsApiKey: ENV.GOOGLE_MAPS_API_KEY,
  });

  const categoryRows = useMemo(() => rows, [rows]);
  const normalizedLookup = useMemo(() => {
    if (!lookupResult) {
      return null;
    }
    if (Array.isArray(lookupResult)) {
      return lookupResult[0] ?? null;
    }
    return lookupResult;
  }, [lookupResult]);
  const mapCenter = useMemo(() => {
    const firstWithCoords = lookupResults.find(
      (item) => item.latitude && item.longitude
    );
    if (firstWithCoords && firstWithCoords.latitude && firstWithCoords.longitude) {
      return { lat: firstWithCoords.latitude, lng: firstWithCoords.longitude };
    }
    return { lat: 34.0522, lng: -118.2437 };
  }, [lookupResults]);

  useEffect(() => {
    if (queryFilters.location) setSelectedLocation(queryFilters.location);
    if (queryFilters.geographyType) setSelectedGeographyType(queryFilters.geographyType);
    if (queryFilters.utilityTerritory) setSelectedUtility(queryFilters.utilityTerritory);
    if (queryFilters.isDac !== undefined) setSelectedIsDac(queryFilters.isDac);
    if (queryFilters.address) setLookupQuery(queryFilters.address);
  }, [queryFilters]);

  useEffect(() => {
    const loadDatabaseOverview = async () => {
      setLoading(true);
      setStatusMessage(null);
      try {
        const response = await api.DataMiner.getAllIncentives();
        if (response.zeosStatus?.toLowerCase() !== "success") {
          throw new Error(response.message || "Failed to load incentives.");
        }
        setRows(aggregateRows(response.data || []));
        setAnalyzed(true);
      } catch (error: any) {
        setRows([]);
        setStatusMessage(error?.message || "Failed to load incentives.");
        setAnalyzed(true);
      } finally {
        setLoading(false);
      }
    };

    loadDatabaseOverview();
  }, [api.DataMiner]);

  const handleAnalyze = async (
    overrideLocation?: string,
    overrideIsDac?: boolean
  ) => {
    setLoading(true);
    setStatusMessage(null);
    try {
      const locationValue = overrideLocation ?? selectedLocation;
      const dacValue = overrideIsDac ?? selectedIsDac;
      const response = await api.DataMiner.matchIncentives({
        categories: [],
        areas: locationValue ? [locationValue] : [],
        types: [],
        isDac: dacValue,
      });
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Analysis failed.");
      }
      const data = response.data || [];
      const aggregated = aggregateRows(data);
      setRows(aggregated);
      setAnalyzed(true);
    } catch (error: any) {
      setRows([]);
      setStatusMessage(error?.message || "Analysis failed.");
      setAnalyzed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleLookup = async () => {
    setLookupStatus(null);
    setLookupResult(null);
    if (!lookupQuery.trim()) {
      setLookupStatus("Enter an address or APN to lookup.");
      return;
    }
    try {
      const response = await api.DataMiner.lookupProperty({
        address: lookupQuery.trim(),
        useLiveLookup: true,
      });
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Lookup failed.");
      }
      setLookupResult(response.data || null);
    } catch (error: any) {
      setLookupStatus(error?.message || "Lookup failed.");
    }
  };

  const runAddressLookup = async (address: string, shouldAnalyze: boolean) => {
    setLookupStatus(null);
    setLookupResult(null);
    try {
      const response = await api.DataMiner.lookupProperty({
        address: address.trim(),
        useLiveLookup: true,
      });
      if (response.zeosStatus?.toLowerCase() !== "success") {
        throw new Error(response.message || "Lookup failed.");
      }
      const data = response.data || null;
      setLookupResult(data);
      if (data) {
        const fallbackLocation = selectedLocation || data.formattedAddress || address;
        const derivedLocation =
          data.county && data.stateCode
            ? `${data.county}, ${data.stateCode}`
            : fallbackLocation;
        setSelectedLocation(derivedLocation);
        if (data.isDac !== undefined && data.isDac !== null) {
          setSelectedIsDac(data.isDac);
        }
      }
      if (shouldAnalyze) {
        const fallbackLocation = selectedLocation || data?.formattedAddress || address;
        await handleAnalyze(
          data?.county && data?.stateCode
            ? `${data.county}, ${data.stateCode}`
            : fallbackLocation,
          data?.isDac ?? undefined
        );
      }
    } catch (error: any) {
      setLookupStatus(error?.message || "Lookup failed.");
    }
  };

  const applyLookupFilter = (result: LookupResult) => {
    const derivedLocation =
      result.county && result.stateCode
        ? `${result.county}, ${result.stateCode}`
        : result.formattedAddress || result.address;
    setSelectedLocation(derivedLocation);
    if (result.county) {
      setSelectedGeographyType("County");
    }
    if (result.isDac !== undefined && result.isDac !== null) {
      setSelectedIsDac(result.isDac);
    }
  };

  const runSampleLookups = async () => {
    setLookupLoading(true);
    setLookupBatchStatus(null);
    const results: LookupResult[] = [];

    for (const item of sampleLookups) {
      try {
        const response = await api.DataMiner.lookupProperty({
          address: item.address,
          useLiveLookup: false,
        });
        if (response.zeosStatus?.toLowerCase() !== "success") {
          results.push({
            ...item,
            status: response.message || "Lookup failed.",
          });
          continue;
        }

        const data = response.data || {};
        results.push({
          ...item,
          formattedAddress: data.formattedAddress,
          county: data.county,
          stateCode: data.stateCode,
          censusTractId: data.censusTractId,
          dacPercentile: data.dacPercentile,
          isDac: data.isDac,
          latitude: data.latitude,
          longitude: data.longitude,
          status: "OK",
        });
      } catch (error: any) {
        results.push({
          ...item,
          status: error?.message || "Lookup failed.",
        });
      }
    }

    setLookupResults(results);
    setLookupLoading(false);
    if (results.length === 0) {
      setLookupBatchStatus("No lookup results returned.");
    }
  };

  useEffect(() => {
    if (!queryFilters.address || hasAppliedAddress.current) {
      return;
    }
    hasAppliedAddress.current = true;
    runAddressLookup(queryFilters.address, true);
  }, [queryFilters.address]);

  return (
    <div className="flex flex-col min-h-screen">
      <DashboardHeader />
      <main className="bg-zeos-white-md p-4 flex-grow">
        <div className="grid grid-cols-12 gap-6 font-sans">
          <div className="col-span-12">
            <h1 className="text-xl font-semibold text-black">
              Data Miner – Geography View
            </h1>
            <p className="text-sm text-gray-600 mt-1">
              Market-level incentive intelligence for Turtle and distributor
              teams.
            </p>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Geography Selection
            </h2>
            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">
                  Location
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2"
                  value={selectedLocation}
                  onChange={(event) => setSelectedLocation(event.target.value)}
                >
                  <option>Los Angeles County, CA</option>
                  <option>Houston, TX (placeholder)</option>
                  <option>Boston, MA (test market)</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">
                  Geography Type
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2"
                  value={selectedGeographyType}
                  onChange={(event) => setSelectedGeographyType(event.target.value)}
                >
                  <option>County</option>
                  <option>City</option>
                  <option>ZIP Code</option>
                  <option>Municipality</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">
                  Utility Territory
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2"
                  value={selectedUtility}
                  onChange={(event) => setSelectedUtility(event.target.value)}
                >
                  <option>LADWP (auto)</option>
                </select>
              </div>
              <div className="col-span-3">
                <label className="block text-xs text-gray-600 mb-1">
                  DAC Status
                </label>
                <select
                  className="w-full border border-gray-300 rounded px-2 py-2"
                  value={
                    selectedIsDac === undefined
                      ? "unknown"
                      : selectedIsDac
                      ? "dac"
                      : "non-dac"
                  }
                  onChange={(event) => {
                    const value = event.target.value;
                    if (value === "dac") setSelectedIsDac(true);
                    else if (value === "non-dac") setSelectedIsDac(false);
                    else setSelectedIsDac(undefined);
                  }}
                >
                  <option value="unknown">Unknown</option>
                  <option value="dac">DAC</option>
                  <option value="non-dac">Non-DAC</option>
                </select>
              </div>
            </div>
          </div>

          <div className="col-span-12 bg-gray-50 border border-gray-200 rounded p-3 text-xs text-gray-700">
            <div className="font-semibold mb-2">Test Links</div>
            <div className="flex flex-wrap gap-2">
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "Los Angeles County, CA",
                  geographyType: "County",
                  utilityTerritory: "LADWP (auto)",
                  categories: ["Energy Storage (BESS)"],
                  isDac: true,
                })}`}
              >
                LA County Storage (DAC)
              </a>
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "Los Angeles County, CA",
                  geographyType: "County",
                  utilityTerritory: "LADWP (auto)",
                  categories: ["Energy Storage (BESS)"],
                  isDac: false,
                })}`}
              >
                LA County Storage (Non-DAC)
              </a>
              <a
                className="text-zeos-orange-dk hover:underline"
                href={`/incentives/data-miner/energy-storage${buildDataMinerQuery({
                  location: "New York County, NY",
                  geographyType: "County",
                  utilityTerritory: "ConEd (auto)",
                  categories: ["Energy Storage (BESS)"],
                })}`}
              >
                NY County Storage
              </a>
            </div>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Dataset Overview
            </h2>
            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-3">
                <div className="text-xs text-gray-500">Total buildings</div>
                <div className="text-lg font-semibold text-black">142,390</div>
              </div>
              <div className="col-span-3">
                <div className="text-xs text-gray-500">Distinct ZIP codes</div>
                <div className="text-lg font-semibold text-black">132</div>
              </div>
              <div className="col-span-3">
                <div className="text-xs text-gray-500">Avg. sq ft</div>
                <div className="text-lg font-semibold text-black">28,400</div>
              </div>
              <div className="col-span-3">
                <div className="text-xs text-gray-500">Median sq ft</div>
                <div className="text-lg font-semibold text-black">11,900</div>
              </div>
            </div>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Search &amp; Filter
            </h2>
            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-4">
                <label className="block text-xs text-gray-600 mb-1">
                  Building Type
                </label>
                <select className="w-full border border-gray-300 rounded px-2 py-2">
                  <option>Commercial</option>
                  <option>Multifamily</option>
                  <option>Industrial</option>
                  <option>Institutional / Municipal</option>
                </select>
              </div>
              <div className="col-span-4">
                <label className="block text-xs text-gray-600 mb-1">
                  Minimum Size (sq ft)
                </label>
                <select className="w-full border border-gray-300 rounded px-2 py-2">
                  <option>&gt; 10,000</option>
                  <option>&gt; 25,000</option>
                  <option>&gt; 50,000</option>
                </select>
              </div>
              <div className="col-span-4">
                <label className="block text-xs text-gray-600 mb-1">
                  Year Built (optional)
                </label>
                <input
                  type="text"
                  placeholder="e.g. 1990–2015"
                  className="w-full border border-gray-300 rounded px-2 py-2"
                />
              </div>
            </div>

            <div className="mt-4 flex items-center gap-3">
              <button
                type="button"
                onClick={() => handleAnalyze()}
                className="bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
                disabled={loading}
              >
                {loading ? "Analyzing..." : "Analyze Incentive Landscape"}
              </button>
              <label className="flex items-center gap-2 text-xs text-gray-700">
                <input
                  type="checkbox"
                  checked={showPriorityOnly}
                  onChange={() => setShowPriorityOnly((prev) => !prev)}
                />
                Show Priority / Disadvantaged Areas
              </label>
              {statusMessage && (
                <span className="text-xs text-red-600">{statusMessage}</span>
              )}
            </div>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Property Lookup
            </h2>
            <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
              <input
                type="text"
                value={lookupQuery}
                onChange={(event) => setLookupQuery(event.target.value)}
                placeholder="Enter address or APN"
                className="w-full md:w-1/2 border border-gray-300 rounded px-2 py-2 text-sm"
              />
              <button
                type="button"
                onClick={handleLookup}
                className="bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
              >
                Lookup Property
              </button>
              {lookupStatus && (
                <span className="text-xs text-red-600">{lookupStatus}</span>
              )}
            </div>
            {normalizedLookup && (
              <div className="mt-4 grid grid-cols-12 gap-4">
                <div className="col-span-12 lg:col-span-5 text-xs text-gray-700">
                  <div className="font-semibold text-gray-900 mb-2">
                    Lookup Summary
                  </div>
                  <div className="space-y-1">
                    <div>
                      <span className="text-gray-500">Address:</span>{" "}
                      {normalizedLookup.formattedAddress ||
                        normalizedLookup.address ||
                        "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">County:</span>{" "}
                      {normalizedLookup.county && normalizedLookup.stateCode
                        ? `${normalizedLookup.county}, ${normalizedLookup.stateCode}`
                        : "—"}
                    </div>
                    <div>
                      <span className="text-gray-500">DAC:</span>{" "}
                      {normalizedLookup.isDac === undefined ||
                      normalizedLookup.isDac === null
                        ? "Unknown"
                        : normalizedLookup.isDac
                        ? "Yes"
                        : "No"}
                    </div>
                    <div>
                      <span className="text-gray-500">Census Tract:</span>{" "}
                      {normalizedLookup.censusTractId || "—"}
                    </div>
                  </div>
                </div>
                <div className="col-span-12 lg:col-span-7">
                  <div className="h-[260px] w-full rounded border border-gray-200 overflow-hidden">
                    {isMapLoaded ? (
                      normalizedLookup.latitude && normalizedLookup.longitude ? (
                        <GoogleMap
                          mapContainerStyle={{ width: "100%", height: "100%" }}
                          center={{
                            lat: normalizedLookup.latitude,
                            lng: normalizedLookup.longitude,
                          }}
                          zoom={13}
                        >
                          <Marker
                            position={{
                              lat: normalizedLookup.latitude,
                              lng: normalizedLookup.longitude,
                            }}
                          />
                        </GoogleMap>
                      ) : (
                        <div className="flex items-center justify-center h-full text-xs text-gray-500">
                          No coordinates returned for this lookup.
                        </div>
                      )
                    ) : (
                      <div className="flex items-center justify-center h-full text-xs text-gray-500">
                        Loading map…
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Sample Lookups &amp; Map
            </h2>
            <p className="text-xs text-gray-600 mb-3">
              Run a batch of 10–20 real LA County addresses across different
              building types. Use a result to auto-fill location and DAC
              filters.
            </p>
            <div className="flex items-center gap-3 mb-3">
              <button
                type="button"
                onClick={runSampleLookups}
                className="bg-zeos-orange-dk text-white px-4 py-2 rounded text-sm"
                disabled={lookupLoading}
              >
                {lookupLoading ? "Running Lookups..." : "Run Sample Lookups"}
              </button>
              {lookupBatchStatus && (
                <span className="text-xs text-red-600">{lookupBatchStatus}</span>
              )}
            </div>

            <div className="grid grid-cols-12 gap-4 text-sm">
              <div className="col-span-12 lg:col-span-5">
                {lookupResults.length === 0 ? (
                  <div className="text-xs text-gray-500">
                    No lookup results yet.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 text-left">
                        <tr>
                          <th className="px-2 py-2 border-b">Site</th>
                          <th className="px-2 py-2 border-b">Type</th>
                          <th className="px-2 py-2 border-b">DAC</th>
                          <th className="px-2 py-2 border-b">County</th>
                          <th className="px-2 py-2 border-b">Action</th>
                        </tr>
                      </thead>
                      <tbody>
                        {lookupResults.map((result) => (
                          <tr key={result.id} className="border-t">
                            <td className="px-2 py-2">
                              <div className="font-semibold text-gray-800">
                                {result.label}
                              </div>
                              <div className="text-[11px] text-gray-500">
                                {result.formattedAddress || result.address}
                              </div>
                              {result.status && result.status !== "OK" && (
                                <div className="text-[11px] text-red-600">
                                  {result.status}
                                </div>
                              )}
                            </td>
                            <td className="px-2 py-2">{result.buildingType}</td>
                            <td className="px-2 py-2">
                              {result.isDac === undefined || result.isDac === null
                                ? "Unknown"
                                : result.isDac
                                ? "Yes"
                                : "No"}
                            </td>
                            <td className="px-2 py-2">
                              {result.county && result.stateCode
                                ? `${result.county}, ${result.stateCode}`
                                : "—"}
                            </td>
                            <td className="px-2 py-2">
                              <button
                                type="button"
                                className="text-zeos-orange-dk hover:underline text-[11px]"
                                onClick={() => applyLookupFilter(result)}
                              >
                                Use for filters
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
              <div className="col-span-12 lg:col-span-7">
                <div className="h-[360px] w-full rounded border border-gray-200 overflow-hidden">
                  {isMapLoaded ? (
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "100%" }}
                      center={mapCenter}
                      zoom={10}
                    >
                      {lookupResults
                        .filter((item) => item.latitude && item.longitude)
                        .map((item) => (
                          <Marker
                            key={item.id}
                            position={{
                              lat: item.latitude as number,
                              lng: item.longitude as number,
                            }}
                          />
                        ))}
                    </GoogleMap>
                  ) : (
                    <div className="flex items-center justify-center h-full text-xs text-gray-500">
                      Loading map…
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="col-span-12 bg-white border border-gray-200 rounded p-4">
            <h2 className="text-sm font-semibold text-black mb-3">
              Incentive Landscape
            </h2>
            {!analyzed ? (
              <p className="text-sm text-gray-600">
                Loading incentive programs from the database.
              </p>
            ) : loading ? (
              <p className="text-sm text-gray-600">Loading incentives…</p>
            ) : categoryRows.length === 0 ? (
              <p className="text-sm text-gray-600">
                No incentive programs returned from the database.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm border border-gray-200">
                  <thead className="bg-gray-100 text-left">
                    <tr>
                      <th className="px-3 py-2 border-b">Category</th>
                      <th className="px-3 py-2 border-b">Program Count</th>
                      <th className="px-3 py-2 border-b">Admin Level</th>
                      <th className="px-3 py-2 border-b">Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categoryRows.map((row) => (
                      <tr
                        key={row.category}
                        className="border-t cursor-pointer hover:bg-gray-50"
                        onClick={() => {
                          const categoryFilter = row.filterCategory ?? row.category;
                          const filters = {
                            categories: categoryFilter ? [categoryFilter] : [],
                            areas: selectedLocation ? [selectedLocation] : [],
                            types: [],
                            isDac: selectedIsDac,
                          };
                          const query = buildDataMinerQuery({
                            location: selectedLocation,
                            geographyType: selectedGeographyType,
                            utilityTerritory: selectedUtility,
                            categories: filters.categories,
                            areas: filters.areas,
                            types: filters.types,
                            isDac: selectedIsDac,
                          });
                          navigate(`/incentives/data-miner/${row.categorySlug}${query}`, {
                            state: {
                              category: row.category,
                              location: selectedLocation,
                              geographyType: selectedGeographyType,
                              utilityTerritory: selectedUtility,
                              isDac: selectedIsDac,
                              filters,
                            },
                          });
                        }}
                      >
                        <td className="px-3 py-2">{row.category}</td>
                        <td className="px-3 py-2">{row.count}</td>
                        <td className="px-3 py-2">{row.admin}</td>
                        <td className="px-3 py-2">{row.notes}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-3 text-xs text-gray-600">
                  {showPriorityOnly
                    ? "Priority areas only: 38% of filtered properties fall in DAC tracts."
                    : "All properties: 18% fall in DAC tracts; priority adders available across storage and electrification programs."}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};

const slugify = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const aggregateRows = (items: IDataMinerIncentiveItem[]): IncentiveRow[] => {
  const map = new Map<string, IncentiveRow>();
  for (const item of items) {
    const category = item.category || "Other";
    const key = category.toLowerCase();
    const existing = map.get(key);
    if (existing) {
      existing.count += 1;
    } else {
      map.set(key, {
        category,
        categorySlug: slugify(category),
        count: 1,
        admin: item.area || "Various",
        notes: item.type || "Program alignment",
      });
    }
  }
  return Array.from(map.values());
};
